import type {
  MonthFocus,
  Plan,
  Profile,
  Space,
  SpaceInvite,
  SpaceMember,
  UserPreferences,
} from '@/domain/types';
import { createId, hashInviteCode, nowIso } from '@/domain/helpers';
import { todayKey, shiftDateKey, yearMonthKey } from '@/domain/dates';
import { DEFAULT_BACKGROUND } from '@/storage/db';

export const DEMO_ALEX_ID = '00000000-0000-4000-8000-000000000001';
export const DEMO_MIA_ID = '00000000-0000-4000-8000-000000000002';
export const DEMO_SPACE_ID = '00000000-0000-4000-8000-000000000010';
export const DEMO_INVITE_CODE = 'TWYME1';

export interface DemoSeed {
  profiles: Profile[];
  space: Space;
  members: SpaceMember[];
  invite: SpaceInvite;
  plans: Plan[];
  monthFocus: MonthFocus[];
  preferences: UserPreferences[];
}

function plan(partial: Partial<Plan> & Pick<Plan, 'ownerId' | 'title'>): Plan {
  const ts = nowIso();
  const id = createId();
  return {
    id,
    clientId: id,
    spaceId: DEMO_SPACE_ID,
    description: '',
    planDate: todayKey(),
    startTime: null,
    endTime: null,
    isAllDay: true,
    availabilityStatus: 'none',
    visibility: 'shared',
    hideDetailsFromPartner: false,
    status: 'active',
    categoryColor: null,
    colorKey: null,
    createdAt: ts,
    updatedAt: ts,
    deletedAt: null,
    clientUpdatedAt: ts,
    ...partial,
  };
}

export async function buildDemoSeed(locale: 'en' | 'ru' = 'en'): Promise<DemoSeed> {
  const ts = nowIso();
  const t = todayKey();
  const ym = yearMonthKey(new Date());

  const titles =
    locale === 'ru'
      ? {
          doctor: 'Приём у врача',
          work: 'Работа до 18:00',
          grocery: 'Продукты',
          movie: 'Вечер кино',
          free: 'Свободный вечер',
          cat: 'Заказать корм коту',
          gym: 'Спортзал',
          trip: 'Поездка на выходные',
          focus: 'Сфокусироваться на доме и отдыхе',
        }
      : {
          doctor: 'Doctor appointment',
          work: 'Work until 6 PM',
          grocery: 'Grocery shopping',
          movie: 'Movie night',
          free: 'Free evening',
          cat: 'Order cat food',
          gym: 'Gym',
          trip: 'Weekend trip',
          focus: 'Focus on home and rest',
        };

  const profiles: Profile[] = [
    {
      id: DEMO_ALEX_ID,
      displayName: 'Alex',
      avatarPath: null,
      accentColor: '#7B8CDE',
      locale,
      createdAt: ts,
      updatedAt: ts,
    },
    {
      id: DEMO_MIA_ID,
      displayName: 'Mia',
      avatarPath: null,
      accentColor: '#6BB3A0',
      locale,
      createdAt: ts,
      updatedAt: ts,
    },
  ];

  const space: Space = {
    id: DEMO_SPACE_ID,
    name: 'twyme-space',
    createdBy: DEMO_ALEX_ID,
    createdAt: ts,
    updatedAt: ts,
  };

  const members: SpaceMember[] = [
    {
      spaceId: DEMO_SPACE_ID,
      userId: DEMO_ALEX_ID,
      joinedAt: ts,
      memberStatus: 'active',
    },
    {
      spaceId: DEMO_SPACE_ID,
      userId: DEMO_MIA_ID,
      joinedAt: ts,
      memberStatus: 'active',
    },
  ];

  const codeHash = await hashInviteCode(DEMO_INVITE_CODE);
  const invite: SpaceInvite = {
    id: createId(),
    spaceId: DEMO_SPACE_ID,
    codeHash,
    codeHint: DEMO_INVITE_CODE,
    expiresAt: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
    status: 'used',
    createdBy: DEMO_ALEX_ID,
    usedAt: ts,
    usedBy: DEMO_MIA_ID,
    createdAt: ts,
  };

  const plans: Plan[] = [
    plan({
      ownerId: DEMO_ALEX_ID,
      title: titles.doctor,
      planDate: t,
      startTime: '15:00',
      endTime: '16:00',
      isAllDay: false,
      availabilityStatus: 'busy',
      hideDetailsFromPartner: true,
      visibility: 'shared',
    }),
    plan({
      ownerId: DEMO_MIA_ID,
      title: titles.work,
      planDate: t,
      startTime: '09:00',
      endTime: '18:00',
      isAllDay: false,
      availabilityStatus: 'busy',
    }),
    plan({
      ownerId: DEMO_ALEX_ID,
      title: titles.grocery,
      planDate: shiftDateKey(t, 1),
      startTime: '11:00',
      endTime: '12:00',
      isAllDay: false,
      visibility: 'shared',
    }),
    plan({
      ownerId: DEMO_MIA_ID,
      title: titles.movie,
      planDate: shiftDateKey(t, 2),
      startTime: '20:00',
      endTime: '22:00',
      isAllDay: false,
      visibility: 'shared',
    }),
    plan({
      ownerId: DEMO_ALEX_ID,
      title: titles.free,
      planDate: shiftDateKey(t, 3),
      availabilityStatus: 'free',
      visibility: 'shared',
    }),
    plan({
      ownerId: DEMO_MIA_ID,
      title: titles.cat,
      planDate: null,
      visibility: 'only_me',
    }),
    plan({
      ownerId: DEMO_ALEX_ID,
      title: titles.gym,
      planDate: shiftDateKey(t, 1),
      startTime: '07:00',
      endTime: '08:00',
      isAllDay: false,
      availabilityStatus: 'busy',
      visibility: 'only_me',
    }),
    plan({
      ownerId: DEMO_ALEX_ID,
      title: titles.trip,
      planDate: shiftDateKey(t, 5),
      visibility: 'shared',
      isAllDay: true,
    }),
  ];

  const monthFocus: MonthFocus[] = [
    {
      id: createId(),
      spaceId: DEMO_SPACE_ID,
      ownerId: DEMO_ALEX_ID,
      yearMonth: ym,
      text: titles.focus,
      visibility: 'shared',
      createdAt: ts,
      updatedAt: ts,
      deletedAt: null,
      clientId: createId(),
      clientUpdatedAt: ts,
    },
  ];

  const basePrefs = (userId: string): UserPreferences => ({
    userId,
    locale,
    timeFormat: locale === 'ru' ? '24h' : '12h',
    weekStartsOn: locale === 'ru' ? 1 : 0,
    notifications: { enabled: false, offsets: [] },
    background: { ...DEFAULT_BACKGROUND },
    onboardingCompleted: true,
    shortcutsHintSeen: false,
    updatedAt: ts,
  });

  return {
    profiles,
    space,
    members,
    invite,
    plans,
    monthFocus,
    preferences: [basePrefs(DEMO_ALEX_ID), basePrefs(DEMO_MIA_ID)],
  };
}
